import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert, Platform, Clipboard, Linking } from 'react-native';
import AppTextInput from '../components/AppTextInput';
import { AuthManager } from '../utils/authManager';

interface WidgetDownloadScreenProps {
  onBack: () => void;
  username?: string;
}

const WidgetDownloadScreen: React.FC<WidgetDownloadScreenProps> = ({ onBack, username }) => {
  const [businessId, setBusinessId] = useState<string>('demo_business');
  const [widgetConfig, setWidgetConfig] = useState({
    position: 'floating',
    theme: 'default',
    buttonText: 'Book Appointment',
    buttonColor: '#667eea',
    showPoweredBy: true,
  });
  const [widgetCode, setWidgetCode] = useState('');
  const [activePlatform, setActivePlatform] = useState('html');

  useEffect(() => {
    loadBusinessId();
  }, []);

  useEffect(() => {
    generateWidgetCode();
  }, [businessId, widgetConfig]);

  const loadBusinessId = async () => {
    try {
      const businessOwner = await AuthManager.getSavedBusinessOwner();
      if (businessOwner && businessOwner.id) {
        setBusinessId(businessOwner.id);
      }
    } catch (error) {
      console.error('Error loading business ID:', error);
    }
  };

  const generateWidgetCode = () => {
    const config = widgetConfig;
    const embedMode = 'fullscreen';
    const siteButtonComment =
      config.position === 'button' || config.position === 'custom'
        ? `

<!-- Your "Book" button (style to match your site). Opens booking on your page — no popup catalog, no new tab: -->
<button type="button" onclick="window.bookaptWidget && bookaptWidget.openBooking()">
  ${config.buttonText.replace(/</g, '')}
</button>`
        : '';
    const code = `<!-- BookAPT Booking Widget -->
<script>
  (function() {
    window.BookAPTConfig = {
      businessId: '${businessId}',
      position: '${config.position}',
      embedMode: '${embedMode}',
      theme: '${config.theme}',
      buttonText: '${config.buttonText.replace(/'/g, "\\'")}',
      buttonColor: '${config.buttonColor}',
      showPoweredBy: ${config.showPoweredBy},
      hideFloatingButton: ${config.position === 'button' || config.position === 'custom'}
    };
  })();
</script>
<script src="https://bookapt-payment-api.onrender.com/widget/bookapt-widget.js" data-business-id="${businessId}" async></script>
<!-- End BookAPT Widget -->${siteButtonComment}`;
    setWidgetCode(code);
  };

  const copyToClipboard = () => {
    Clipboard.setString(widgetCode);
    Alert.alert('✅ Success', 'Widget code copied to clipboard!');
  };

  const downloadWidget = () => {
    // For React Native, we'll show the code in an alert or save to a file
    Alert.alert(
      'Widget Code',
      widgetCode,
      [
        { text: 'Copy Code', onPress: copyToClipboard },
        { text: 'OK' },
      ]
    );
  };

  const features = [
    { name: 'Appointment Booking', widget: '✅ Yes', platform: '✅ Yes', status: '✅ Available' },
    { name: 'Payment Processing', widget: '✅ Yes', platform: '✅ Yes', status: '✅ Available' },
    { name: 'Product Sales', widget: '✅ Yes', platform: '✅ Yes', status: '✅ Available' },
    { name: 'SMS Payment Requests', widget: '✅ Yes', platform: '✅ Yes', status: '✅ Available' },
    { name: 'Customer Management', widget: '⚠️ View Only', platform: '✅ Full', status: '✅ Available' },
    { name: 'Calendar Integration', widget: '❌ No', platform: '✅ Yes', status: '✅ Available' },
    { name: 'Automatic Reminders', widget: '✅ Yes', platform: '✅ Yes', status: '✅ Available' },
    { name: 'Worker Assignment', widget: '⚠️ Limited', platform: '✅ Full', status: '✅ Available' },
    { name: 'Recurring Appointments', widget: '✅ Yes', platform: '✅ Yes', status: '✅ Available' },
    { name: 'In-App Chat', widget: '❌ No', platform: '✅ Yes', status: '✅ Available' },
    { name: 'AI Chat Booking', widget: '✅ Yes', platform: '✅ Yes', status: '✅ Available' },
    { name: 'Custom Service Requests', widget: '✅ Yes', platform: '✅ Yes', status: '✅ Available' },
    { name: '30-Second Booking', widget: '✅ Yes', platform: '✅ Yes', status: '✅ Available' },
    { name: 'Confirm Booking Option', widget: '✅ Yes', platform: '✅ Yes', status: '✅ Available' },
    { name: 'Analytics & Reports', widget: '❌ No', platform: '✅ Yes', status: '✅ Available' },
    { name: 'Settings & Configuration', widget: '❌ No', platform: '✅ Yes', status: '✅ Available' },
    { name: 'Mobile Apps', widget: '❌ No', platform: '✅ Yes', status: '✅ Available' },
  ];

  const platforms = [
    { id: 'wordpress', name: 'WordPress', instructions: [
      'Go to your WordPress admin dashboard',
      'Navigate to Appearance → Theme Editor',
      'Select footer.php from the right sidebar',
      'Paste the widget code before the closing </body> tag',
      'Click Update File',
      'Alternatively, use a plugin like "Insert Headers and Footers"',
    ]},
    { id: 'shopify', name: 'Shopify', instructions: [
      'Go to your Shopify admin dashboard',
      'Navigate to Online Store → Themes',
      'Click Actions → Edit Code',
      'Open theme.liquid',
      'Paste the widget code before the closing </body> tag',
      'Click Save',
    ]},
    { id: 'wix', name: 'Wix', instructions: [
      'Go to your Wix editor',
      'Click Add → Embed → Embed a Widget',
      'Click Enter Code',
      'Paste the widget code',
      'Click Update',
      'Position the widget where you want it',
    ]},
    { id: 'squarespace', name: 'Squarespace', instructions: [
      'Go to your Squarespace editor',
      'Click Edit on the page where you want the widget',
      'Click + Add Block',
      'Select Code',
      'Paste the widget code',
      'Click Apply',
    ]},
    { id: 'html', name: 'HTML/Other', instructions: [
      'Open your website\'s HTML file(s)',
      'Find the closing </body> tag',
      'Paste the widget code before </body>',
      'Save the file',
      'Upload to your web server',
    ]},
  ];

  return (
    <View style={styles.container}>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={onBack} style={styles.backButton}>
            <Text style={styles.backButtonText}>← Back</Text>
          </TouchableOpacity>
          <Text style={styles.title}>📦 Download BookAPT Widget</Text>
          <Text style={styles.subtitle}>Add booking to your website in 2 minutes</Text>
        </View>

        {/* Feature Comparison Table */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>✅ Complete Feature Access</Text>
          <Text style={styles.sectionDescription}>
            When you install the BookAPT widget, you get <Text style={styles.bold}>ALL BookAPT features</Text>.
            The widget is your customer booking interface, and the BookAPT platform provides full business management.
          </Text>

          <View style={styles.tableContainer}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Feature</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Widget</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Platform</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1 }]}>Status</Text>
            </View>
            {features.map((feature, index) => (
              <View key={index} style={styles.tableRow}>
                <Text style={[styles.tableCell, { flex: 2, fontWeight: '600' }]}>{feature.name}</Text>
                <Text style={[styles.tableCell, { flex: 1.5 }]}>{feature.widget}</Text>
                <Text style={[styles.tableCell, { flex: 1.5 }]}>{feature.platform}</Text>
                <Text style={[styles.tableCell, { flex: 1, color: '#28a745', fontWeight: '600' }]}>{feature.status}</Text>
              </View>
            ))}
            <View style={[styles.tableRow, styles.totalRow]}>
              <Text style={[styles.tableCell, { flex: 5, fontWeight: '600' }]}>Total Features Available</Text>
              <Text style={[styles.tableCell, { flex: 1, color: '#28a745', fontWeight: '600' }]}>17/17 (100%)</Text>
            </View>
          </View>

          <View style={styles.summaryContainer}>
            <View style={styles.summaryBox}>
              <Text style={styles.summaryTitle}>📱 Widget (Your Website)</Text>
              <Text style={styles.summaryText}>Customer-facing booking interface on your website. Customers can book appointments 24/7.</Text>
            </View>
            <View style={styles.summaryBox}>
              <Text style={styles.summaryTitle}>💻 BookAPT Platform</Text>
              <Text style={styles.summaryText}>Full business management system. Manage appointments, payments, customers, analytics, and more.</Text>
            </View>
            <View style={[styles.summaryBox, styles.highlightBox]}>
              <Text style={[styles.summaryTitle, styles.highlightText]}>🎯 Complete Solution</Text>
              <Text style={[styles.summaryText, styles.highlightText]}>
                <Text style={styles.bold}>Together, you get all 17 features!</Text> Widget for customer booking + Platform for business management.
              </Text>
            </View>
          </View>
        </View>

        {/* Widget Customization */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Customize Your Widget</Text>
          <Text style={styles.sectionDescription}>Configure your widget to match your website's design and branding.</Text>

          <View style={styles.configContainer}>
            <View style={styles.configGroup}>
              <Text style={styles.configLabel}>Position:</Text>
              <Text style={styles.configHint}>
                Use Button if the booking panel should open only from your own &quot;Book appointment&quot; control (no floating bubble or BookAPT logo on the page).
              </Text>
              <View style={styles.buttonGroup}>
                {['floating', 'inline', 'button'].map((pos) => (
                  <TouchableOpacity
                    key={pos}
                    style={[
                      styles.configButton,
                      widgetConfig.position === pos && styles.configButtonActive,
                    ]}
                    onPress={() => setWidgetConfig({ ...widgetConfig, position: pos })}
                  >
                    <Text
                      style={[
                        styles.configButtonText,
                        widgetConfig.position === pos && styles.configButtonTextActive,
                      ]}
                    >
                      {pos.charAt(0).toUpperCase() + pos.slice(1)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.configGroup}>
              <Text style={styles.configLabel}>Theme:</Text>
              <View style={styles.buttonGroup}>
                {['default', 'dark', 'light'].map((theme) => (
                  <TouchableOpacity
                    key={theme}
                    style={[
                      styles.configButton,
                      widgetConfig.theme === theme && styles.configButtonActive,
                    ]}
                    onPress={() => setWidgetConfig({ ...widgetConfig, theme })}
                  >
                    <Text
                      style={[
                        styles.configButtonText,
                        widgetConfig.theme === theme && styles.configButtonTextActive,
                      ]}
                    >
                      {theme.charAt(0).toUpperCase() + theme.slice(1)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.configGroup}>
              <Text style={styles.configLabel}>Button Text:</Text>
              <AppTextInput
                style={styles.textInput}
                value={widgetConfig.buttonText}
                onChangeText={(text) => setWidgetConfig({ ...widgetConfig, buttonText: text })}
                placeholder="Book Appointment"
              />
            </View>

            <View style={styles.configGroup}>
              <Text style={styles.configLabel}>Show "Powered by BookAPT":</Text>
              <TouchableOpacity
                style={styles.checkboxContainer}
                onPress={() => setWidgetConfig({ ...widgetConfig, showPoweredBy: !widgetConfig.showPoweredBy })}
              >
                <View style={[styles.checkbox, widgetConfig.showPoweredBy && styles.checkboxChecked]}>
                  {widgetConfig.showPoweredBy && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkboxLabel}>Enable</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {/* Widget Code */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Your Widget Code</Text>
          <Text style={styles.sectionDescription}>
            Copy this code and paste it before the closing {'</body>'} tag on your website:
          </Text>

          <View style={styles.codeContainer}>
            <ScrollView horizontal style={styles.codeScrollView}>
              <Text style={styles.codeText} selectable>{widgetCode}</Text>
            </ScrollView>
            <TouchableOpacity style={styles.copyButton} onPress={copyToClipboard}>
              <Text style={styles.copyButtonText}>📋 Copy Code</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Download Options */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Download Widget Package</Text>
          <Text style={styles.sectionDescription}>
            Download a complete widget package with all files and installation instructions.
          </Text>
          <TouchableOpacity style={styles.downloadButton} onPress={downloadWidget}>
            <Text style={styles.downloadButtonText}>📥 Download Widget Package</Text>
          </TouchableOpacity>
        </View>

        {/* Platform-Specific Instructions */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Platform-Specific Instructions</Text>
          
          <ScrollView horizontal style={styles.platformTabs} showsHorizontalScrollIndicator={false}>
            {platforms.map((platform) => (
              <TouchableOpacity
                key={platform.id}
                style={[
                  styles.platformTab,
                  activePlatform === platform.id && styles.platformTabActive,
                ]}
                onPress={() => setActivePlatform(platform.id)}
              >
                <Text
                  style={[
                    styles.platformTabText,
                    activePlatform === platform.id && styles.platformTabTextActive,
                  ]}
                >
                  {platform.name}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <View style={styles.platformInstructions}>
            {platforms.map((platform) => {
              if (activePlatform !== platform.id) return null;
              return (
                <View key={platform.id} style={styles.platformContent}>
                  <Text style={styles.platformTitle}>{platform.name} Installation</Text>
                  {platform.instructions.map((instruction, index) => (
                    <View key={index} style={styles.instructionItem}>
                      <Text style={styles.instructionNumber}>{index + 1}.</Text>
                      <Text style={styles.instructionText}>{instruction}</Text>
                    </View>
                  ))}
                </View>
              );
            })}
          </View>
        </View>

        {/* Support Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Need Help?</Text>
          <Text style={styles.sectionDescription}>
            If you need assistance installing the widget, we're here to help!
          </Text>
          <View style={styles.supportContainer}>
            <TouchableOpacity 
              style={styles.supportButton}
              onPress={async () => {
                const email = 'info@orynsolutions.io';
                const url = `mailto:${email}?subject=Widget Installation Help`;
                try {
                  const supported = await Linking.canOpenURL(url);
                  if (supported) {
                    await Linking.openURL(url);
                  } else {
                    Alert.alert('Contact Support', `Email: ${email}`);
                  }
                } catch (error) {
                  Alert.alert('Contact Support', `Email: ${email}`);
                }
              }}
            >
              <Text style={styles.supportButtonText}>📧 Contact Support</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.supportButton}
              onPress={async () => {
                const url = 'https://orynsolutions.io/help';
                try {
                  const supported = await Linking.canOpenURL(url);
                  if (supported) {
                    await Linking.openURL(url);
                  } else {
                    Alert.alert('Documentation', 'Visit: https://orynsolutions.io/help');
                  }
                } catch (error) {
                  Alert.alert('Documentation', 'Visit: https://orynsolutions.io/help');
                }
              }}
            >
              <Text style={styles.supportButtonText}>📚 Documentation</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.supportButton}
              onPress={async () => {
                const url = 'https://orynsolutions.io/help';
                try {
                  const supported = await Linking.canOpenURL(url);
                  if (supported) {
                    await Linking.openURL(url);
                  } else {
                    Alert.alert('Video Tutorial', 'Visit: https://orynsolutions.io/help');
                  }
                } catch (error) {
                  Alert.alert('Video Tutorial', 'Visit: https://orynsolutions.io/help');
                }
              }}
            >
              <Text style={styles.supportButtonText}>🎥 Video Tutorial</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f7fa',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 40,
  },
  header: {
    marginBottom: 30,
    alignItems: 'center',
  },
  backButton: {
    alignSelf: 'flex-start',
    padding: 10,
    marginBottom: 10,
  },
  backButtonText: {
    fontSize: 16,
    color: '#667eea',
    fontWeight: '600',
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 10,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 18,
    color: '#666',
    textAlign: 'center',
  },
  section: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 10,
  },
  sectionDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 20,
    lineHeight: 20,
  },
  bold: {
    fontWeight: '600',
  },
  tableContainer: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 20,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#667eea',
    padding: 12,
  },
  tableHeaderCell: {
    color: 'white',
    fontWeight: '600',
    fontSize: 12,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    padding: 12,
    backgroundColor: 'white',
  },
  totalRow: {
    backgroundColor: '#f8f9fa',
    fontWeight: '600',
  },
  tableCell: {
    fontSize: 12,
    color: '#333',
  },
  summaryContainer: {
    marginTop: 20,
  },
  summaryBox: {
    backgroundColor: '#f8f9fa',
    padding: 15,
    borderRadius: 8,
    marginBottom: 15,
    borderLeftWidth: 4,
    borderLeftColor: '#667eea',
  },
  highlightBox: {
    backgroundColor: '#667eea',
  },
  summaryTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  highlightText: {
    color: 'white',
  },
  summaryText: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  configContainer: {
    marginTop: 10,
  },
  configGroup: {
    marginBottom: 20,
  },
  configLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  configHint: {
    fontSize: 12,
    color: '#666',
    marginBottom: 10,
    lineHeight: 18,
  },
  buttonGroup: {
    flexDirection: 'row',
    gap: 10,
    flexWrap: 'wrap',
  },
  configButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: 'white',
  },
  configButtonActive: {
    backgroundColor: '#667eea',
    borderColor: '#667eea',
  },
  configButtonText: {
    fontSize: 12,
    color: '#333',
  },
  configButtonTextActive: {
    color: 'white',
    fontWeight: '600',
  },
  textInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    backgroundColor: 'white',
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: '#667eea',
    borderRadius: 4,
    marginRight: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#667eea',
  },
  checkmark: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  checkboxLabel: {
    fontSize: 14,
    color: '#333',
  },
  codeContainer: {
    backgroundColor: '#1e1e1e',
    borderRadius: 8,
    padding: 15,
    marginTop: 15,
    position: 'relative',
  },
  codeScrollView: {
    maxHeight: 200,
  },
  codeText: {
    fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
    fontSize: 11,
    color: '#d4d4d4',
    lineHeight: 16,
  },
  copyButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    backgroundColor: '#667eea',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  copyButtonText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },
  downloadButton: {
    backgroundColor: '#28a745',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 15,
  },
  downloadButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  platformTabs: {
    marginBottom: 20,
  },
  platformTab: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: 'white',
    marginRight: 10,
  },
  platformTabActive: {
    backgroundColor: '#667eea',
    borderColor: '#667eea',
  },
  platformTabText: {
    fontSize: 14,
    color: '#333',
    fontWeight: '600',
  },
  platformTabTextActive: {
    color: 'white',
  },
  platformInstructions: {
    marginTop: 10,
  },
  platformContent: {
    // Content is conditionally rendered, no need for display styles
  },
  platformTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#333',
    marginBottom: 15,
  },
  instructionItem: {
    flexDirection: 'row',
    marginBottom: 12,
    alignItems: 'flex-start',
  },
  instructionNumber: {
    fontSize: 14,
    fontWeight: '600',
    color: '#667eea',
    marginRight: 10,
    minWidth: 20,
  },
  instructionText: {
    flex: 1,
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  supportContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 15,
  },
  supportButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 6,
    flex: 1,
    minWidth: '30%',
    alignItems: 'center',
  },
  supportButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
});

export default WidgetDownloadScreen;

